/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {filePermissionsPermission} from '@ion/dls/src/editors/features/PermissionsUtils';
import {useUserPermissions} from '@ion/query/src/hooks/useUserPermissions';
import {getAction, getFilePermissionsPermission} from '../helpers';
import useFilePermissionsFilter from './useFilePermissionsFilter';

/**
 * Provides a utility hook to check the current user role has tagged
 * any one of the Create/Assign FILE PERMISSIONS feature enabled.
 *
 * ```typescript
 *
 * import {useFilePermissionsAction} from '@ion/applications/src/hooks/useFilePermissionsAction';
 *
 * const hasFilePermissions = useFilePermissionsAction();
 *
 * ```
 *
 * @param   Features A reference to the application specific `ApplicationFeatures`
 *          implementation..
 * @param   isDeviceFile When provided returns CREATE action to filter based on
 *          permissions.
 * @param   isEditor When provided returns user tagged permissions list.
 * @param   isDeviceFileEditor When provided returns CREATE action to filter based on
 *          permissions.
 *
 */
export const useFilePermissionsAction = ({
  Features,
  isDeviceFile = false,
  isEditor = false,
  isDeviceFileEditor = false
}) => {
  const action             = getAction(isDeviceFile, isDeviceFileEditor);
  const userPermissions    = useUserPermissions()?.userPermissions ?? [];
  const filePermissions    = useFilePermissionsFilter(Features, action);
  const filePermission     = getFilePermissionsPermission(filePermissions);
  const permissionResource = new Set(filePermissionsPermission);
  const permissions        = userPermissions?.filter?.(item =>
    filePermission?.includes?.(
      item?.permission?.resource
    ));
  const showPermissions    = userPermissions?.filter?.(item =>
    item?.permission && permissionResource?.has?.(item?.permission?.resource)
  );
  const permission         = permissions?.filter?.(item => item?.permission?.action === action);
  if (isEditor) {
    return Array.from(
      new Set(permission?.map(({permission}) => permission.resource))
    );
  }
  return !showPermissions?.some(({permission}) => permission.action === action);
};

export default useFilePermissionsAction;
